import { inject, Injectable, NgZone } from '@angular/core'
import { filter, Subscription, switchMap, take, tap, timer } from 'rxjs'
import { AutoRebootConfig } from '../interfaces/htv-config'
import { MonitoringService } from './monitoring.service'
import { LogService } from 'htv-lib'
import { PowerState } from '../interfaces/power-state'
import { PlatformService } from '../platforms/platform.service'

@Injectable({ providedIn: 'root' })
export class AutoRebootService {
  private readonly ngZone = inject(NgZone)
  private readonly logService = inject(LogService)
  private readonly platformService = inject(PlatformService)
  private readonly powerState$ = inject(MonitoringService).powerState
  private readonly startedAt = Date.now()

  private sub?: Subscription

  configure(autoReboot: AutoRebootConfig): void {
    this.sub?.unsubscribe()

    if (!autoReboot.enabled) {
      this.logService.info(`[AutoRebootService] Auto reboot is disabled`)
      return
    }

    const hour = autoReboot.localHour
    const minute = autoReboot.localMinute

    if (!this.isValidHour(hour) || !this.isValidMinute(minute)) {
      this.logService.error(
        `[AutoRebootService] Invalid auto reboot configuration: ${autoReboot.localHour}:${autoReboot.localMinute}`
      )
      return
    }

    this.logService.info(`[AutoRebootService] Auto reboot configured at ${hour}:${minute}`)
    this.sub = this.ngZone.runOutsideAngular(() =>
      timer(0, 30_000)
        .pipe(
          filter(() => this.isDue(hour, minute)),
          take(1),
          tap(() => this.logService.info(`[AutoRebootService] Pending reboot, waiting for standby`)),
          switchMap(() => this.powerState$),
          filter(powerState => powerState === PowerState.STANDBY),
          take(1)
        )
        .subscribe(() => {
          this.logService.info(`[AutoRebootService] Standby detected, rebooting`)
          this.platformService.reboot()
        })
    )
  }

  private isValidHour(hour: number): boolean {
    return Number.isInteger(hour) && (hour as number) >= 0 && (hour as number) <= 23
  }

  private isValidMinute(minute: number): boolean {
    return Number.isInteger(minute) && (minute as number) >= 0 && (minute as number) <= 59
  }

  private isDue(hour: number, minute: number): boolean {
    const target = new Date()
    target.setHours(hour, minute, 0, 0)
    return Date.now() >= target.getTime() && this.startedAt < target.getTime()
  }
}
